import { customInstance } from "@repo/api-client";

/**
 * Client contract for the verified Google place-match surface (`/api/v1/venues/{venueId}/place-match`,
 * `rwg-place-matching`, §C.1). Goes through `@repo/api-client`'s fetch mutator (bearer token + typed
 * `ApiError`), NOT a raw fetch — a 409 stale write comes back as `ApiError` carrying the RFC 9457
 * problem. Thin accessors matching the `scheduleApi` pattern; the Orval client regenerates from the
 * backend OpenAPI at serial integration (`just gen`).
 */

/** The venue's own record, shown side-by-side with each Google candidate for comparison. */
export interface VenueRecord {
  legalName: string;
  displayName: string;
  category: string;
  formattedAddress: string;
}

/** One Google Places candidate (display-only; only the chosen placeId is ever persisted). */
export interface PlaceCandidate {
  placeId: string;
  name: string;
  formattedAddress: string;
  category?: string | null;
}

export interface PlaceMatchStatus {
  /** UNMATCHED | UNVERIFIED | VERIFIED | STALE | NOT_FOUND. */
  status: string;
  placeId?: string | null;
  verifiedAt?: string | null;
  reconfirmDueSince?: string | null;
  /** R5: a gating field changed — a manager re-check is due (the venue stays gated in). */
  needsReconfirm: boolean;
  /** R2: true iff VERIFIED — the venue is on RwG surfaces. */
  gatedIn: boolean;
  version: number;
}

export interface PlaceMatchSearchResult {
  searchable: boolean;
  venue: VenueRecord | null;
  candidates: PlaceCandidate[];
}

const base = (venueId: string) => `/api/v1/venues/${encodeURIComponent(venueId)}/place-match`;

export const placeMatchApi = {
  getStatus: (venueId: string) =>
    customInstance<PlaceMatchStatus>(base(venueId), { method: "GET" }),

  search: (venueId: string) =>
    customInstance<PlaceMatchSearchResult>(`${base(venueId)}/search`, { method: "POST" }),

  confirm: (venueId: string, placeId: string, expectedVersion: number) =>
    customInstance<PlaceMatchStatus>(`${base(venueId)}/confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ placeId, expectedVersion }),
    }),

  markNone: (venueId: string, expectedVersion: number) =>
    customInstance<PlaceMatchStatus>(`${base(venueId)}/none`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ expectedVersion }),
    }),
};

export const placeMatchKeys = {
  status: (venueId: string) => ["venue", venueId, "place-match"] as const,
};
